import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

// Types
import { User } from 'Shared/constants/types';
import { Workspace } from 'Shared/types/Workspace.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Actions
import { removeFromWorkspace } from 'Shared/actions/workspaces';

// Components
import { WpButton } from 'Wpds/components/button';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

// Styles
import './WorkspaceMemberItem.scss';

type Props = {
  user: User;
  workspace: Workspace;
};

const WorkspaceMemberItemRemove = ({ user, workspace }: Props) => {
  const dispatch = useDispatch();
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const [isRemovingUser, toggleIsRemovingUser] = useToggle(false);

  const { id: currentUserId } = useCurrentUser();
  const workspaceId = workspace.id;
  const workspaceName = workspace.name;

  const isRemovingCurrentUser = user.id === currentUserId;
  const canRemove = workspace.users.length > 1;

  const handleRemoveUser = useCallback(() => {
    toggleIsRemovingUser();
    dispatch(removeFromWorkspace({ id: workspaceId, userId: user.id })).then(() => {
      toggleIsRemovingUser();
      toggleIsModalOpen();
    });
  }, [user, dispatch, workspaceId]);

  if (!canRemove) return null;

  return (
    <>
      <WpButton variant="tertiary" size="sm" color="danger" onClick={toggleIsModalOpen} qaTest={`remove-${user.email}`}>
        {isRemovingCurrentUser ? 'Leave' : 'Remove'}
      </WpButton>
      {isModalOpen && (
        <ConfirmModal
          isOpen={isModalOpen}
          onToggle={toggleIsModalOpen}
          title="Remove member from workspace"
          message={`Are you sure you want to remove ${
            isRemovingCurrentUser ? 'yourself' : user.fullName
          } from ${workspaceName}?`}
          onConfirmClick={handleRemoveUser}
          confirmButtonLabel="Remove"
          isProcessing={isRemovingUser}
        />
      )}
    </>
  );
};

export default WorkspaceMemberItemRemove;
